import { Anchor, Button, Center, Container, Group, Stack, Text, TextInput } from '@mantine/core';
import { createFileRoute, LoaderFnContext, useRouter } from '@tanstack/react-router';
import { PageHeader } from '@/components/PageHeader';
import { isPersonalTenant } from '@/lib/auth';
import { MyRouterContext } from '@/App';
import { MainContainer } from '@/components/MainContainer';
import { useCallback, useState } from 'react';
import { notifyError } from '@/lib/notification';
import { logger } from '@/lib/logger';
import { modals } from '@mantine/modals';
import { useForm } from 'react-hook-form';
import { IconUserPlus } from '@tabler/icons-react';
import { ProjectApiKeyList } from './-components/ProjectApiKeyList';
import { PendingInitialize } from '@/components/PendingInitialize';
import { CodeHighlight } from '@mantine/code-highlight';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';

export const Route = createFileRoute('/_main/_project/project/api-keys/')({
  loader,
  component: Component,
  pendingComponent: () => <PendingInitialize title="APIキー" />,
});

async function loader({
  context: { adminClient, projectId },
}: LoaderFnContext<{ projectId: string }, { project: string | undefined }, MyRouterContext>) {
  let apiKeys = null;
  try {
    if (projectId) {
      apiKeys = await adminClient
        .settings_v1_get_project_api_keys({
          params: { project_id: projectId },
        })
        .then((res) => res.items);
    }
  } catch (error) {
    logger.warn(error);
  }
  return {
    adminClient,
    apiKeys,
    projectId,
  };
}

function Component() {
  const { adminClient, apiKeys, projectId } = Route.useLoaderData();
  const { project, userInfo } = Route.parentRoute.useLoaderData();
  const router = useRouter();
  const isArchived = project?.status === 'archived';
  const isOwner = project?.role === 'owner';
  const readonly = isArchived || !isOwner;

  const createApiKey = useCallback(
    async (name: string) => {
      try {
        const res = await adminClient.settings_v1_create_project_api_key(
          {
            name: name,
          },
          {
            params: { project_id: projectId! },
          }
        );
        await router.load();
        modals.closeAll();
        openGenerateApiKeyResult(res.api_key);
      } catch (error) {
        logger.error(error);
        notifyError(`APIキー ${name} の作成に失敗しました。`);
      }
    },
    [adminClient, router, projectId]
  );

  const openGenerateApiKeyResult = (secret: string) => {
    modals.open({
      title: 'APIキーのシークレットが発行されました',
      children: (
        <>
          <div className="text-sm">
            シークレットは、この画面を閉じると再表示できません。安全な場所に保存してください。
          </div>
          <div className="my-10">
            <div className="text-sm mb-1">APIキーのシークレット</div>
            <CodeHighlight
              classNames={{ root: 'border border-plamo-border rounded-lg' }}
              code={secret}
            ></CodeHighlight>
          </div>
        </>
      ),
    });
  };

  const openCreateApiKeyModal = () => {
    modals.open({
      size: 'md',
      title: 'APIキー新規作成',
      children: <CreateApiKeyForm onSubmit={({ name }) => createApiKey(name)} />,
    });
  };

  return (
    <MainContainer>
      <PageHeader
        title="APIキー"
        description={
          <>
            {isPersonalTenant(userInfo) ? '' : 'プロジェクトに紐づく'}APIキーを管理します。PLaMo API
            を実行する際に使用します。
            <br />
            APIの詳細な使い方については、
            <Anchor href="https://docs.plamo.preferredai.jp/" target="_blank">
              API ドキュメント
            </Anchor>
            を参照してください。
          </>
        }
      />
      {isArchived && <Text c="red">このプロジェクトはアーカイブされています。</Text>}
      {!projectId && <Text>右上のプロジェクト選択から、プロジェクトを選択してください。</Text>}
      {projectId && !apiKeys && (
        <Text>
          プロジェクトが見つかりませんでした。
          <br />
          右上のプロジェクト選択から、プロジェクトを選択してください。
        </Text>
      )}
      {projectId && apiKeys && (
        <>
          {/* 無料プランのキーはプロファイルの個人 API キー 1 本だけ（admin も quota 0 で断る） */}
          {!isPersonalTenant(userInfo) && (
            <Group justify="end">
              <Button
                color="blue"
                size="sm"
                leftSection={<IconUserPlus style={{ width: '70%', height: '70%' }} />}
                onClick={() => openCreateApiKeyModal()}
                disabled={readonly}
              >
                APIキーを追加する
              </Button>
            </Group>
          )}
          <ProjectApiKeyList
            adminClient={adminClient}
            apiKeys={apiKeys}
            projectId={projectId}
            readonly={readonly}
          />
        </>
      )}
    </MainContainer>
  );
}

const inputSchema = z.object({
  name: z.string().min(1, '必須項目です。').max(63, '63文字以内で入力してください。'),
});
type Inputs = z.infer<typeof inputSchema>;

type FormProps = {
  onSubmit: (data: Inputs) => Promise<void>;
};

function CreateApiKeyForm({ onSubmit }: FormProps) {
  const [loading, setLoading] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { isValid, errors },
  } = useForm<Inputs>({
    resolver: zodResolver(inputSchema),
    mode: 'onChange',
    defaultValues: {
      name: '',
    },
  });
  const submit = (data: Inputs) => {
    setLoading(true);
    return onSubmit(data).finally(() => setLoading(false));
  };

  return (
    <Container m="lg">
      <form onSubmit={handleSubmit(submit)}>
        <Center>
          <Stack w="100%">
            <TextInput label="APIキー名" {...register('name')} error={errors.name?.message} />
            <Text size="xs">※ 管理しやすい任意の名前をつけて作成できます。</Text>
            <Button type="submit" size="sm" disabled={!isValid} mt="lg" loading={loading}>
              作成
            </Button>
          </Stack>
        </Center>
      </form>
    </Container>
  );
}
